import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  UploadSimple, Image, VideoCamera, Microphone, FileText, Trash,
  DownloadSimple, Eye, FunnelSimple, AirplaneTilt, Anchor,
  MagnifyingGlass, X, CaretDown
} from '@phosphor-icons/react';
import { useDropzone } from 'react-dropzone';

const API = '/api';
const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

const CATEGORY_ICONS = {
  image: Image,
  video: VideoCamera,
  audio: Microphone,
  document: FileText,
  other: FileText,
};

const CATEGORY_COLORS = {
  image: 'bg-purple-100 text-purple-700',
  video: 'bg-red-100 text-red-700',
  audio: 'bg-amber-100 text-amber-700',
  document: 'bg-blue-100 text-blue-700',
  other: 'bg-slate-100 text-slate-600',
};

const EVIDENCE_TAGS = [
  'Wreckage', 'Flight Recorder', 'Cockpit/Bridge', 'Witness',
  'Maintenance Record', 'Weather Data', 'ATC Recording', 'CCTV',
  'Radar Data', 'Medical', 'Structural', 'Personal Effects',
  'Navigation', 'Communication', 'Other'
];

function FilePreview({ evidence, apiBase }) {
  const { file_category, investigation_id, stored_name } = evidence;
  const fileUrl = `${apiBase}/evidence/file/${investigation_id}/${stored_name}`;

  if (file_category === 'image') {
    return <img src={fileUrl} alt={evidence.file_name} className="w-full h-40 object-cover rounded-lg" crossOrigin="use-credentials" />;
  }
  if (file_category === 'video') {
    return (
      <video controls className="w-full h-40 rounded-lg bg-black" crossOrigin="use-credentials">
        <source src={fileUrl} />
      </video>
    );
  }
  if (file_category === 'audio') {
    return (
      <div className="w-full h-40 rounded-lg bg-slate-900 flex flex-col items-center justify-center p-4">
        <Microphone size={32} className="text-amber-400 mb-2" />
        <audio controls className="w-full" crossOrigin="use-credentials">
          <source src={fileUrl} />
        </audio>
      </div>
    );
  }
  const Icon = CATEGORY_ICONS[file_category] || FileText;
  return (
    <div className="w-full h-40 rounded-lg bg-slate-100 flex flex-col items-center justify-center">
      <Icon size={40} className="text-slate-400 mb-2" />
      <span className="text-xs text-slate-500">{evidence.file_ext}</span>
    </div>
  );
}

function UploadProgress({ progress, fileName }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="upload-progress">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700 truncate max-w-xs">{fileName}</span>
        <span className="text-xs text-slate-500">{Math.round(progress)}%</span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2">
        <div
          className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

export default function Evidence() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === 'admin' || user?.role === 'iic';

  const [investigations, setInvestigations] = useState([]);
  const [selectedInv, setSelectedInv] = useState('');
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryTag, setCategoryTag] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/investigations`, { withCredentials: true });
        setInvestigations(res.data || []);
        if (res.data?.length > 0) setSelectedInv(res.data[0].id);
      } catch (e) { /* error handled silently */ }
    })();
  }, []);

  const loadEvidence = useCallback(async () => {
    if (!selectedInv) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/evidence/investigation/${selectedInv}`, { withCredentials: true });
      setEvidence(res.data || []);
    } catch (e) { /* error handled silently */ } finally { setLoading(false); }
  }, [selectedInv]);

  useEffect(() => { loadEvidence(); }, [loadEvidence]);

  const onDrop = useCallback((accepted) => {
    setSelectedFiles(accepted);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxFiles: 10,
  });

  const uploadFile = async (file) => {
    setUploadFileName(file.name);
    setUploadProgress(0);

    if (file.size <= CHUNK_SIZE) {
      // Simple upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('investigation_id', selectedInv);
      formData.append('description', description);
      formData.append('category', categoryTag);

      await axios.post(`${API}/evidence/simple-upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    } else {
      // Chunked upload
      const initRes = await axios.post(`${API}/evidence/init-upload`, {
        file_name: file.name,
        file_size: file.size,
        investigation_id: selectedInv,
        description,
        category: categoryTag,
      }, { withCredentials: true });

      const { upload_id, total_chunks } = initRes.data;

      for (let i = 0; i < total_chunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const chunkForm = new FormData();
        chunkForm.append('chunk', chunk);

        await axios.post(`${API}/evidence/upload-chunk/${upload_id}/${i}`, chunkForm, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploadProgress(Math.round(((i + 1) / total_chunks) * 100));
      }

      await axios.post(`${API}/evidence/complete-upload/${upload_id}`, {}, { withCredentials: true });
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || !selectedInv) return;
    setUploading(true);
    try {
      for (const file of selectedFiles) {
        await uploadFile(file);
      }
      setSelectedFiles([]);
      setDescription('');
      setCategoryTag('');
      setUploadProgress(0);
      setUploadFileName('');
      loadEvidence();
    } catch (e) {
      alert(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this evidence permanently?')) return;
    try {
      await axios.delete(`${API}/evidence/${id}`, { withCredentials: true });
      setEvidence(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = evidence.filter(e => {
    if (filterCategory !== 'all' && e.file_category !== filterCategory) return false;
    if (searchTerm && !e.file_name.toLowerCase().includes(searchTerm.toLowerCase()) && !e.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: evidence.length,
    images: evidence.filter(e => e.file_category === 'image').length,
    videos: evidence.filter(e => e.file_category === 'video').length,
    audio: evidence.filter(e => e.file_category === 'audio').length,
    documents: evidence.filter(e => e.file_category === 'document').length,
  };

  return (
    <div className="max-w-6xl" data-testid="evidence-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Evidence Management</h1>
        <p className="text-sm text-slate-500 mt-1">Upload and manage investigation evidence files</p>
      </div>

      {/* Investigation Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Select Investigation</Label>
        <Select value={selectedInv} onValueChange={setSelectedInv} data-testid="investigation-select">
          <SelectTrigger className="mt-1 rounded-lg" data-testid="investigation-select-trigger">
            <SelectValue placeholder="Select investigation..." />
          </SelectTrigger>
          <SelectContent>
            {investigations.map(inv => (
              <SelectItem key={inv.id} value={inv.id}>
                <span className="flex items-center gap-2">
                  {inv.sector === 'aviation' ? <AirplaneTilt size={14} /> : <Anchor size={14} />}
                  {inv.investigation_number} — {inv.title}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedInv && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Total', count: stats.total, color: 'bg-slate-800 text-white' },
              { label: 'Photos', count: stats.images, color: 'bg-purple-50 text-purple-800' },
              { label: 'Videos', count: stats.videos, color: 'bg-red-50 text-red-800' },
              { label: 'Audio', count: stats.audio, color: 'bg-amber-50 text-amber-800' },
              { label: 'Docs', count: stats.documents, color: 'bg-blue-50 text-blue-800' },
            ].map(s => (
              <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>
                <div className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>{s.count}</div>
                <div className="text-xs opacity-80">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Upload Area */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6" data-testid="upload-section">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-3">Upload Evidence</h2>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400'
              }`}
              data-testid="evidence-dropzone"
            >
              <input {...getInputProps()} data-testid="file-input" />
              <UploadSimple size={36} className="mx-auto text-slate-400 mb-2" />
              {selectedFiles.length > 0 ? (
                <div>
                  <p className="text-blue-700 font-medium">{selectedFiles.length} file(s) selected</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedFiles.map(f => f.name).join(', ')}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-600">{isDragActive ? 'Drop files here...' : 'Drag & drop files, or click to select'}</p>
                  <p className="text-xs text-slate-400 mt-1">Images, videos, audio, documents — up to 10 files. Large files supported via chunked upload.</p>
                </div>
              )}
            </div>

            {selectedFiles.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-600">Description</Label>
                  <Input
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    className="mt-1 rounded-lg"
                    placeholder="Describe this evidence..."
                    data-testid="evidence-description"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Category Tag</Label>
                  <Select value={categoryTag} onValueChange={setCategoryTag}>
                    <SelectTrigger className="mt-1 rounded-lg" data-testid="category-tag-select">
                      <SelectValue placeholder="Select tag..." />
                    </SelectTrigger>
                    <SelectContent>
                      {EVIDENCE_TAGS.map(tag => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 flex gap-3">
                  <Button
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg"
                    onClick={handleUpload}
                    disabled={uploading}
                    data-testid="upload-btn"
                  >
                    <UploadSimple size={16} className="mr-2" />
                    {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
                  </Button>
                  <Button variant="outline" className="rounded-lg" onClick={() => setSelectedFiles([])} data-testid="clear-files-btn">
                    <X size={14} className="mr-1" /> Clear
                  </Button>
                </div>
              </div>
            )}

            {uploading && <div className="mt-4"><UploadProgress progress={uploadProgress} fileName={uploadFileName} /></div>}
          </div>

          {/* Filter Bar */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-xs">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9 rounded-lg text-sm"
                placeholder="Search evidence..."
                data-testid="search-evidence"
              />
            </div>
            <div className="flex gap-1">
              {['all', 'image', 'video', 'audio', 'document'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilterCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filterCategory === cat ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Evidence Grid */}
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading evidence...</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <FileText size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{evidence.length === 0 ? 'No evidence uploaded yet' : 'No files match your filter'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="evidence-grid">
              {filtered.map(item => {
                const Icon = CATEGORY_ICONS[item.file_category] || FileText;
                return (
                  <div key={item.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`evidence-card-${item.id}`}>
                    <FilePreview evidence={item} apiBase={API} />
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[item.file_category] || CATEGORY_COLORS.other}`}>
                          {item.file_category}
                        </span>
                        {item.category_tag && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{item.category_tag}</span>
                        )}
                      </div>
                      <h3 className="text-sm font-medium text-slate-800 truncate" title={item.file_name}>{item.file_name}</h3>
                      {item.description && <p className="text-xs text-slate-500 truncate mt-0.5">{item.description}</p>}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-slate-400">{item.file_size_display}</span>
                        <div className="flex gap-1">
                          <a
                            href={`${API}/evidence/file/${item.investigation_id}/${item.stored_name}`}
                            download
                            className="p-1 rounded hover:bg-slate-100 text-slate-500"
                            data-testid={`download-${item.id}`}
                          >
                            <DownloadSimple size={14} />
                          </a>
                          {canDelete && (
                            <button onClick={() => handleDelete(item.id)} className="p-1 rounded hover:bg-red-50 text-red-400" data-testid={`delete-${item.id}`}>
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {item.uploaded_by_name || item.uploaded_by} — {new Date(item.uploaded_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
